/**
 * wastewaterNarrativeService.ts
 *
 * Short, chart-contextual prose for the Wastewater sector page.
 * Each function returns an array of brief paragraph strings (typically 1–2).
 */

import type { WastewaterSummaryRow } from './wastewaterDataService';
import type {
  WastewaterPercentileResult,
  WastewaterPeerCity,
  WastewaterTimeSeries,
  WastewaterTreatmentPoint,
} from './wastewaterAnalyticsService';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 0): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function pct(n: number, decimals = 1): string {
  return `${fmt(Math.abs(n), decimals)}%`;
}

function percentileWord(p: number): string {
  if (p <= 0.1) return 'among the lowest in California';
  if (p <= 0.3) return 'below the California average';
  if (p <= 0.55) return 'near the California average';
  if (p <= 0.75) return 'above the California average';
  return 'among the highest in California';
}

function trendWord(pctChange: number): string {
  if (Math.abs(pctChange) < 3) return 'remained relatively stable';
  if (pctChange > 0) return `increased by ${pct(pctChange)}`;
  return `decreased by ${pct(pctChange)}`;
}

function confidenceSentence(tier: number | undefined): string {
  if (tier == null) return '';
  if (tier <= 2) return 'This estimate is based on measured facility data and has high confidence.';
  if (tier === 3) return 'This estimate uses attributed facility volumes and has moderate confidence.';
  if (tier === 4) return 'This estimate is based on a matched supplier with an inferred facility and has moderate confidence.';
  return 'This is a preliminary estimate using the statewide per-capita default; treat with lower confidence.';
}

// ─── Overview narrative — one sentence per chart view ───────────────────────

export interface OverviewNarrativeInput {
  location: string;
  row: WastewaterSummaryRow | null;
  percentile: WastewaterPercentileResult;
  totalCommunityEmissions: number | null;
  timeSeries: WastewaterTimeSeries;
  activeChartView?: string;
}

export function generateWastewaterOverviewNarrative(input: OverviewNarrativeInput): string[] {
  const { location, row, percentile, totalCommunityEmissions, timeSeries, activeChartView = 'timeline' } = input;

  if (!row) {
    return [`Wastewater emissions have not yet been calculated for ${location}.`];
  }

  const emissions = row.wastewater_emissions_mtco2e;
  const perCap = row.wastewater_emissions_per_capita;
  const year = row.inventory_year;

  const confidence = confidenceSentence(row.best_tier);

  switch (activeChartView) {
    case 'timeline': {
      const firstYear = timeSeries.points[0]?.year ?? 2005;
      const trendDesc = timeSeries.pctChange != null ? ` and have ${trendWord(timeSeries.pctChange)} since ${firstYear}` : '';
      const popNote = 'Years outside 2020 are population-scaled estimates; annual fluctuations reflect Census population revisions, which can be pronounced in university towns and cities with seasonal residents.';
      return [
        `${location} produced ${fmt(emissions)} MTCO₂e from wastewater treatment in ${year}${trendDesc}. ${confidence}`,
        popNote,
      ];
    }
    case 'ghg_species': {
      const ch4 = row.ch4_mtco2e ?? emissions * 0.28;
      const n2o = row.n2o_mtco2e ?? emissions * 0.72;
      const n2oShare = emissions > 0 ? (n2o / emissions) * 100 : 72;
      const dominant = n2o >= ch4 ? 'N₂O' : 'CH₄';
      return [
        `Wastewater GHG emissions are dominated by ${dominant} (${pct(n2oShare)} of total). ` +
        `Nitrous oxide is the primary gas because California cities use aerobic secondary treatment, which generates N₂O through nitrification but suppresses methane. ${confidence}`,
      ];
    }
    case 'community': {
      const share = totalCommunityEmissions && totalCommunityEmissions > 0
        ? (emissions / totalCommunityEmissions) * 100
        : null;
      return share != null
        ? [`Wastewater accounts for ${pct(share)} of ${location}'s total community GHG inventory — typically a small share compared to transportation and energy, but a sector where cities have direct operational leverage. ${confidence}`]
        : [`The chart compares wastewater's contribution to ${location}'s overall community GHG inventory. ${confidence}`];
    }
    case 'peers': {
      const percWord = percentile.percentile != null ? percentileWord(percentile.percentile) : 'comparable to similar cities';
      const perCapStr = perCap != null ? `${perCap.toLocaleString('en-US', { maximumFractionDigits: 4 })} MTCO₂e per resident, ${percWord}` : 'comparable to similar cities';
      return [`${location}'s per-capita wastewater emissions are ${perCapStr}. ${confidence}`];
    }
    default:
      return [`${location} produced ${fmt(emissions)} MTCO₂e from wastewater treatment in ${year}. ${confidence}`];
  }
}

// ─── Trend narrative (for timeline chart) ────────────────────────────────────

export function generateWastewaterTrendNarrative(
  location: string,
  timeSeries: WastewaterTimeSeries,
  row: WastewaterSummaryRow | null
): string[] {
  if (!row || timeSeries.points.length === 0) return [];
  const firstPt = timeSeries.points[0];
  const lastPt = timeSeries.points[timeSeries.points.length - 1];
  const trendDesc = timeSeries.pctChange != null ? trendWord(timeSeries.pctChange) : 'changed over the period';
  return [
    `Estimated wastewater emissions for ${location} have ${trendDesc} from ${firstPt?.year ?? '?'} to ${lastPt?.year ?? '?'}. ` +
    `All years except 2020 are modelled by scaling the measured baseline with annual Census population estimates.`,
  ];
}

// ─── GHG Breakdown narrative ──────────────────────────────────────────────────

export function generateWastewaterGHGNarrative(row: WastewaterSummaryRow | null): string[] {
  if (!row) return [];
  const ch4 = row.ch4_mtco2e ?? row.wastewater_emissions_mtco2e * 0.28;
  const n2o = row.n2o_mtco2e ?? row.wastewater_emissions_mtco2e * 0.72;
  const total = row.wastewater_emissions_mtco2e;
  const n2oShare = total > 0 ? (n2o / total) * 100 : 72;
  return [
    `N₂O accounts for ${pct(n2oShare)} of total wastewater GHG emissions. ` +
    `Aerobic secondary treatment — standard in California — suppresses methane but generates N₂O through nitrification. ` +
    `N₂O's global warming potential is 273× that of CO₂ (IPCC AR6), making it dominant by weight even when volume is modest.`,
  ];
}

// ─── Treatment Technology narrative ──────────────────────────────────────────

export function generateWastewaterTreatmentNarrative(
  location: string,
  breakdown: WastewaterTreatmentPoint[]
): string[] {
  if (breakdown.length === 0) {
    return [`Treatment process breakdown is not available for ${location}; standard ICLEI USCP Appendix F factors are applied.`];
  }
  const primary = breakdown[0];
  return [
    breakdown.length === 1
      ? `${location}'s wastewater is treated primarily through ${primary.treatment_level}.`
      : `${location}'s wastewater flows to multiple treatment processes; the table shows each process's share of attributed volume and emissions.`,
  ];
}

// ─── Community Share narrative ────────────────────────────────────────────────

export function generateWastewaterCommunityShareNarrative(
  location: string,
  wastewaterMT: number | null,
  totalMT: number | null
): string[] {
  if (!wastewaterMT || !totalMT) return [];
  const share = totalMT > 0 ? (wastewaterMT / totalMT) * 100 : 0;
  return [
    `Wastewater represents ${pct(share)} of ${location}'s total community GHG inventory. ` +
    `Transportation and energy typically account for the largest shares; wastewater is a smaller but locally controllable source.`,
  ];
}

// ─── Relevance to Climate Action narrative ────────────────────────────────────

export interface RelevanceNarrativeInput {
  location: string;
  row: WastewaterSummaryRow | null;
  totalCommunityEmissions: number | null;
  breakdown: WastewaterTreatmentPoint[];
}

export function generateWastewaterRelevanceNarrative(input: RelevanceNarrativeInput): string[] {
  const { location, row } = input;
  if (!row) {
    return [`Wastewater emissions have not yet been quantified for ${location}.`];
  }
  return [
    `Cities can reduce wastewater emissions through utility partnerships, process optimization, and — where anaerobic digesters are in use — biogas capture. Water conservation also reduces treatment volumes and associated emissions.`,
  ];
}

// ─── Equity narrative ─────────────────────────────────────────────────────────

export function generateWastewaterEquityNarrative(location: string): string[] {
  return [
    `Wastewater GHG inventory data is attributed to the jurisdiction served, not to the physical location of the treatment facility. Regional plants may serve multiple cities; the emissions share shown here reflects attributed treatment volume, not proximity to the plant.`,
  ];
}

// ─── Peer comparison narrative ───────────────────────────────────────────────

export function generateWastewaterPeerNarrative(
  location: string,
  peers: WastewaterPeerCity[],
  row: WastewaterSummaryRow | null,
  percentile: WastewaterPercentileResult
): string[] {
  if (!row || peers.length === 0) {
    return [`Peer city comparison not available for ${location}.`];
  }
  const percWord = percentile.percentile != null ? percentileWord(percentile.percentile) : 'comparable to similar cities';
  return [`${location} is ${percWord} for per-capita wastewater emissions among California cities of similar size.`];
}
